import { useCallback, useState } from "react"
import useSWR from "swr"
import { useAuthUser, useHasFeatureFlag } from "../Auth"
import { ErrorBoundary } from "@sembark-travel/ui/error-boundary"
import { Dialog } from "@sembark-travel/ui/dialog"
import {
	Box,
	CheckboxInput,
	Component,
	Icons,
	Inline,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui/base"
import { ITrip } from "./store"
import { useXHR } from "@sembark-travel/xhr"
import { IComment } from "../Comments"
import {
	formatDate,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { ShareViaWhatsApp } from "@sembark-travel/ui/share"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui/copy-to-clipboard"
import { EmailContent } from "../shared/EmailContent"

type TShareVia = "whatsapp" | "email"

export function ShareTripToDMCInDialog({
	trip,
	isValidating,
	children,
}: {
	trip: ITrip
	isValidating?: boolean
	children: (props: { onClick: () => void }) => React.ReactNode
}) {
	const [activeTab, setActiveTab] = useState<undefined | TShareVia>(undefined)
	const isTripEnquiryProcessEnabled = useHasFeatureFlag("trip_enquiry_process")

	if (!isTripEnquiryProcessEnabled) {
		return null
	}

	return (
		<>
			{children({ onClick: () => setActiveTab("whatsapp") })}

			<Dialog
				open={Boolean(activeTab)}
				lg
				onClose={() => setActiveTab(undefined)}
				title="Share Enquiry"
			>
				<ErrorBoundary>
					<ShareAgentToDMC
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						trip={trip}
						isValidating={isValidating}
					/>
				</ErrorBoundary>
			</Dialog>
		</>
	)
}

function OptionsBar({
	options,
	isValidating,
}: {
	options: Array<{
		id: string
		label: string
		checked: boolean
		onChange: () => void
	}>
	isValidating?: boolean
}) {
	if (options.length === 0) {
		return null
	}

	return (
		<Box
			position="sticky"
			top="0"
			padding="4"
			zIndex="10"
			bgColor="subtle"
			borderBottomWidth="1"
			boxShadow="sm"
		>
			<Inline flexWrap="wrap" gap="4">
				{options.map((option) => (
					<Inline key={option.id} as="label" gap={"2"}>
						<CheckboxInput
							checked={option.checked}
							onChange={option.onChange}
						/>{" "}
						{option.label}
					</Inline>
				))}
				{isValidating && (
					<Box>
						<Icons.Refresh spin />
					</Box>
				)}
			</Inline>
		</Box>
	)
}

export function ShareAgentToDMC({
	activeTab,
	setActiveTab,
	trip,
	isValidating,
}: {
	activeTab: undefined | TShareVia
	setActiveTab: (activeTab: TShareVia | undefined) => void
	trip: ITrip
	isValidating?: boolean
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const [emailSubject, setEmailSubject] = useState("")
	const {
		data: commentsData,
		isLoading: isCommentsLoading,
		isValidating: isCommentsValidating,
	} = useSWR<{
		data: IComment[]
	}>(`/trips/${trip.id}/comments`, () =>
		xhr.get(`/trips/${trip.id}/comments`).then((resp) => resp.data)
	)
	const comments: IComment[] | undefined = commentsData?.data || undefined

	const fetchEmailContent = useCallback(
		async (params: object) => {
			return await xhr
				.get<{
					subject: string
					content: string
					options: Array<{ name: string; included: 1 | 0 }>
				}>(`/trips/${trip.id}/request-enquiry-as-email`, {
					params,
				})
				.then((resp) => {
					setEmailSubject(resp.data.subject)
					return resp.data
				})
		},
		[trip.id, xhr]
	)

	if (isCommentsLoading || isCommentsValidating) {
		return <Spinner alignCenter padding="4" />
	}

	const baseWhatsappContent = `Dear Sir / Ma'am,

Greetings from *${trip.tenant_brand?.short_name || user?.tenant?.short_name}*!!!

We are looking to arrange a travel package for our guests. Kindly share a suitable proposal based on details outlined below at the earliest.

----------
*Package Requirements (Trip ID: ${trip.id})*
----------

*Destination: ${trip.destinations.map((d) => d.name).join(", ")}*

*Start Date: ${formatDate(
		localOrUtcTimestampToLocalDate(trip.start_date_local, trip.start_date),
		"DD MMM, YYYY"
	)}*

*Trip Duration: ${trip.nights} Nights, ${trip.days} Days*

*Pax: ${trip.pax_string}*

`
	return (
		<Tabs layout="col">
			<TabsList>
				<TabItem
					active={activeTab === "whatsapp"}
					onClick={() => setActiveTab("whatsapp")}
				>
					<Icons.Annotation /> WhatsApp
				</TabItem>
				<TabItem
					active={activeTab === "email"}
					onClick={() => setActiveTab("email")}
				>
					<Icons.Mail /> Email
				</TabItem>
			</TabsList>

			<TabContent active={activeTab === "whatsapp"}>
				<Component
					initialState={{
						includeLatestComment: comments ? comments.length > 0 : false,
					}}
				>
					{({ state, setState }) => {
						const whatsappContent = `${baseWhatsappContent}${
							state.includeLatestComment && comments && comments.length > 0
								? `\n_*Note*_\n------\n${comments[0].body}\n------`
								: ""
						}`

						return (
							<Box position="relative">
								<Box paddingX="4" paddingY="2">
									<Text fontSize="sm">
										<Icons.LightBulb opacity="70" /> Use toggles to customize
										the content according to your needs.
									</Text>
								</Box>
								<OptionsBar
									options={[
										...(comments && comments.length > 0
											? [
													{
														id: "latest-comment",
														label: "Latest Followup",
														checked: state.includeLatestComment,
														onChange: () =>
															setState({
																...state,
																includeLatestComment:
																	!state.includeLatestComment,
															}),
													},
												]
											: []),
									]}
									isValidating={isValidating}
								/>
								<Box roundedBottom="md" overflow="hidden">
									<ShareViaWhatsApp
										text={whatsappContent}
										trackingContentType={"trip"}
										trackingContentId={"trip_details"}
										disabled={isValidating}
									/>
								</Box>
							</Box>
						)
					}}
				</Component>
			</TabContent>

			<TabContent active={activeTab === "email"} position="relative">
				<Box style={{ minHeight: "90vh" }}>
					<Stack gap="3">
						<Box
							paddingX="4"
							paddingY="2"
							borderWidth={"1"}
							backgroundColor={"subtle"}
						>
							<Text as="p" fontSize="sm" color="muted">
								<Icons.LightBulb /> Copy the subject and body to your Email
								Composer and make changes if required before sending.
							</Text>
						</Box>
						<CopyToClipboard<HTMLDivElement>>
							{({ nodeToCopy, copyNodeTextToClipboard }) => (
								<Stack paddingX="4" gap="1">
									<Inline justifyContent="between" alignItems="center">
										<Text fontWeight="semibold">Subject</Text>
										<CopyToClipboardButton
											size="sm"
											level="primary"
											onClick={async () => {
												return copyNodeTextToClipboard()
											}}
										>
											<Icons.ClipboardCopy /> Copy Subject
										</CopyToClipboardButton>
									</Inline>
									<Box
										paddingY="2"
										paddingX="4"
										borderWidth="1"
										rounded="md"
										bgColor="subtle"
									>
										<Box ref={nodeToCopy}>{emailSubject || "..."}</Box>
									</Box>
								</Stack>
							)}
						</CopyToClipboard>
						<Box>
							<Box marginLeft="4" paddingY={"1"}>
								<Text fontWeight="semibold">Body</Text>
							</Box>
							<EmailContent
								paddingY="4"
								copyTrackId="trip_dmc_request_enquiry"
								contentCacheKey={`/trips/${trip.id}/request-enquiry-as-email`}
								fetchContent={fetchEmailContent}
								defaultInclusionOptions={[
									{
										name: "Latest Followup",
										included: comments && comments.length > 0 ? 1 : 0,
									},
								]}
							/>
						</Box>
					</Stack>
				</Box>
			</TabContent>
		</Tabs>
	)
}
